import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute } from "@esWebAPI/es/model/Feature";

export default class FilterControlUtil {
    /**
     * 
     * @param attributes 
     * @param criteria 
     * 
     * @returns 
     */
    private getAttribute(attributes: Attribute[], criteria: Criteria | Criteria[]): Attribute | undefined {
        if (Array.isArray(criteria)) {
            const attrs = criteria.map((crit) => this.getAttribute(attributes, crit));
            const first = attrs[0]; // can we trust the first value ? No, do sanity check
            const areSameAttrs = attrs.every((attr) => attr === first);
            if (!areSameAttrs) {
                es.log.error("Filter can not be on multiple attributes");
            }
            return first;
        }

        const attrName = es.util.criteria.getAttrs(criteria);
        if (attrName.length === 0) {
            return undefined;
        }

        if (attrName.length > 1 && criteria.$type !== "TerritoryLevel") { // We match 2 arguments for Territory criteria
            es.log.error("Multiple attributes for given criteria"); // this should be impossible
        }

        return attributes.find((attr) => attr.name === attrName[0]);
    }

    /**
     * 
     * @param attributes 
     * @param criteria 
     * 
     * @returns 
     */
    private getAttributes(attributes: Attribute | Attribute[], criteria: Criteria | Criteria[]): Attribute | Attribute[] | undefined {
        if (!Array.isArray(attributes)) {
            attributes = [attributes];
        }

        if (Array.isArray(criteria)) {
            // Return attribute(s) used in each criterion with no duplicate.

            const attrs: Attribute[] = [];
            criteria.forEach(c => {
                let criteriaAttrs = this.getAttributes(attributes, c);
                if (Array.isArray(criteriaAttrs)) {
                    criteriaAttrs.forEach(attr => {
                        if (!attrs.find(a => a.name === attr.name)) {
                            attrs.push(attr);
                        }
                    });
                } else {
                    if (!attrs.find(a => a.name === criteriaAttrs.name)) {
                        attrs.push(criteriaAttrs);
                    }
                }
            })

            return attrs.length === 1 ? attrs[0] : attrs;
        }

        const attrNames = es.util.criteria.getAttrs(criteria);
        if (attrNames.length === 0) {
            return undefined;
        }

        // We match 2 arguments for Territory criteria, more for And/Or
        if (attrNames.length > 1 && criteria.$type !== "TerritoryLevel" && criteria.$type !== "And" && criteria.$type !== "Or") {
            es.log.error("Multiple attributes for given criteria"); // this should be impossible
        }

        if (attrNames.length === 1) {
            return attributes.find((attr) => {
                return attr.name === attrNames[0];
            });
        }

        return attributes.filter((attr) => {
            return attrNames.includes(attr.name);
        });
    }

    /**
     * 
     * @param entityCriteria 
     * @param filterCriteria 
     * 
     * @returns 
     */
    removeFilterCriteria(entityCriteria: Criteria, filterCriteria: Criteria): Criteria | null {
        const attr = es.util.criteria.getAttrs(filterCriteria);
        if (attr.length > 1) {
            es.log.warn("Removing criteria with more than one attribute", entityCriteria, attr);
        }

        let partialCriteria;
        if (!attr.length || filterCriteria.$type === "TerritoryLevel") {
            partialCriteria = {$type: filterCriteria.$type};
        } else {
            partialCriteria = {attr: attr[0]};
        }

        return es.util.criteria.remove(entityCriteria, partialCriteria);
    }

    /**
     * 
     * @param attributes 
     * @param criteria 
     * 
     * @returns 
     */
    getAttributeForCriteria(attributes: Attribute[], criteria: Criteria): Attribute | undefined {
        return this.getAttribute(attributes, criteria);
    }

    /**
     * 
     * @param attributes 
     * @param criteria 
     * 
     * @returns 
     */
    getAttributesForCriteria(attributes: Attribute[], criteria: Criteria): Attribute | Attribute[] | undefined {
        return this.getAttributes(attributes, criteria);
    }

    /**
     * Group criterias per attribute name
     * @param criterias
     * @returns {Array} an Array of array of criteria
     */
    groupByAttribute(criterias: Criteria[], drillDown = true): Criteria[] {
        const nonAttributeCriteria: Criteria[] = [];
        const orderedKeys: string[] = [];
        const criteriaPerAttr: Record<string, Criteria[]> = {};

        const add = (attr: string | null, crit: Criteria) => {
            if (!attr) {
                nonAttributeCriteria.push(crit);
            } else if (criteriaPerAttr[attr]) {
                criteriaPerAttr[attr].push(crit);
            } else {
                orderedKeys.push(attr);
                criteriaPerAttr[attr] = [crit];
            }
        };

        criterias.forEach((crit) => {
            const attrs = es.util.criteria.getAttrs(crit);

            if (attrs.length > 1 && crit.$type !== "And" && crit.$type !== "Or") {
                es.log.warn("Unexpected criteria with multiple attribute", attrs);
            }

            if (crit.$type === "And" && crit.criteria.find(c => c.$type === "Interacts")) {
                add(null, crit);
            } else if ((crit.$type === "And" || crit.$type === "Or") && drillDown) {
                this.groupByAttribute(crit.criteria, false).forEach(c => {
                    if (Array.isArray(c)) {
                        const newCrit = {$type: crit.$type, criteria: c};
                        const attr = es.util.criteria.getAttrs(newCrit)[0];
                        add(attr, newCrit);
                    } else {
                        const attr = es.util.criteria.getAttrs(c)[0];
                        add(attr, c);
                    }
                });
            } else {
                add(attrs[0], crit)
            }
        });

        orderedKeys.forEach((key) => {
            // if it's a group, keep as is if not make it a simple one
            const criteria = criteriaPerAttr[key].length === 1 ? criteriaPerAttr[key][0] : criteriaPerAttr[key];
            nonAttributeCriteria.push(criteria);
        });

        return nonAttributeCriteria;
    }

    /**
     * 
     * @param filters 
     * @param type 
     * @param attrName 
     * 
     * @returns 
     */
    find(filters: any[], type: string, attrName: string): any {
        for (let filter of filters) {
            if (filter.getType() === "Or" || filter.getType() === "And") {
                return this.find(filter.getFilters(), type, attrName);
            } else if (filter.getType() === type && filter.getAttrName() === attrName) {
                return filter;
            }
        }

        return null;
    }

    /**
     * 
     * @param filters 
     * @param filter 
     * 
     * @returns 
     */
    parentOf(filters: any[], filter: any): any {
        let res = null;
        for (let f of filters.filter(f => f.getType() === "Or" || f.getType() === "And")) {
            if (f.getFilters().includes(filter)) {
                return f;
            } else {
                res = this.parentOf(f.getFilters(), filter);
            }
        }

        return res;
    }
}
